import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Users, Trophy, ChevronRight, Search, Filter, X, Plus, Edit, Trash2, UserPlus, Copy, Edit2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import './Management.css';

const Management = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  
  // Initialize activeCard from URL parameter, default to 'proposals'
  const [activeCard, setActiveCard] = useState(
    sectionParam === 'gamification' || sectionParam === 'organizations' ? sectionParam : 'proposals'
  );
  const [proposals, setProposals] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [userPoints, setUserPoints] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  const [loading, setLoading] = useState(false);

  // Update activeCard when URL parameter changes
  useEffect(() => {
    if (sectionParam === 'gamification' || sectionParam === 'organizations') {
      setActiveCard(sectionParam);
    }
  }, [sectionParam]);

  useEffect(() => {
    loadData();
  }, [activeCard]);

  // Refresh proposals when page becomes visible (e.g. user returns to tab after working in Outline Builder)
  useEffect(() => {
    const onVisible = () => {
      if (activeCard === 'proposals') loadData();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [activeCard]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeCard === 'proposals') {
        const response = await api.get('/dashboard/accessible-proposals/');
        console.log('Proposals response:', response.data);
        setProposals(response.data.proposals || []);
      } else if (activeCard === 'organizations') {
        const orgResponse = await api.get('/orgs/list/');
        setOrganizations(orgResponse.data.organizations || []);
        
        // Load teams for each org
        const teamsData = [];
        for (const org of orgResponse.data.organizations || []) {
          try {
            const teamResponse = await api.get(`/orgs/${org.id}/teams/list/`);
            teamsData.push(...(teamResponse.data.teams || []));
          } catch (e) {
            console.error(`Error loading teams for org ${org.id}:`, e);
          }
        }
        setTeams(teamsData);
      } else if (activeCard === 'gamification') {
        const pointsResponse = await api.get('/gamification/points/');
        setUserPoints(pointsResponse.data);
        
        const badgesResponse = await api.get('/gamification/badges/');
        setUserBadges(badgesResponse.data.badges || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="management-container">
      <div className="management-header">
        <h1>Management</h1>
        <p>Manage your proposals, organizations, and track your achievements</p>
      </div>

      {/* Card Navigation */}
      <div className="management-cards">
        <div
          className={`management-card ${activeCard === 'proposals' ? 'active' : ''}`}
          onClick={() => {
            setActiveCard('proposals');
            setSearchParams({});
          }}
        >
          <div className="card-icon">
            <FileText size={24} />
          </div>
          <div className="card-content">
            <h3>Proposals Management</h3>
            <p>View and manage all your proposals</p>
          </div>
          <ChevronRight size={20} className="card-arrow" />
        </div>

        <div
          className={`management-card ${activeCard === 'organizations' ? 'active' : ''}`}
          onClick={() => {
            setActiveCard('organizations');
            setSearchParams({ section: 'organizations' });
          }}
        >
          <div className="card-icon">
            <Users size={24} />
          </div>
          <div className="card-content">
            <h3>Organisation</h3>
            <p>Manage organizations and teams</p>
          </div>
          <ChevronRight size={20} className="card-arrow" />
        </div>

        <div
          className={`management-card ${activeCard === 'gamification' ? 'active' : ''}`}
          onClick={() => {
            setActiveCard('gamification');
            setSearchParams({ section: 'gamification' });
          }}
        >
          <div className="card-icon">
            <Trophy size={24} />
          </div>
          <div className="card-content">
            <h3>Points & Badges</h3>
            <p>Points, badges, and leaderboards</p>
          </div>
          <ChevronRight size={20} className="card-arrow" />
        </div>
      </div>

      {/* Content Area */}
      <div className="management-content">
        {activeCard === 'proposals' && (
          <ProposalsCard 
            proposals={proposals} 
            loading={loading} 
            onRefresh={loadData}
          />
        )}
        {activeCard === 'organizations' && (
          <OrganizationsCard
            organizations={organizations}
            teams={teams}
            loading={loading}
            onRefresh={loadData}
          />
        )}
        {activeCard === 'gamification' && (
          <GamificationCard
            userPoints={userPoints}
            badges={userBadges}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

// Proposals Management Card
const ProposalsCard = ({ proposals, loading, onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [copyValue, setCopyValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const filteredProposals = proposals
    .filter(p => {
      const displayName = p.display_name || p.title;
      const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (p.client_name && p.client_name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const aDate = a.updated_at || a.created_at || '';
      const bDate = b.updated_at || b.created_at || '';
      return bDate.localeCompare(aDate); // newest first
    });

  const statusCounts = proposals.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  const handleRenameClick = (proposal) => {
    setSelectedProposal(proposal);
    setRenameValue(proposal.title);
    setShowRenameModal(true);
  };

  const handleRenameConfirm = async () => {
    if (!selectedProposal || !renameValue.trim()) {
      toast.error('Please enter a valid name');
      return;
    }

    if (renameValue.trim() === selectedProposal.title) {
      setShowRenameModal(false);
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.patch(`/proposal/plan/${encodeURIComponent(selectedProposal.title)}/rename`, {
        title: renameValue.trim()
      });
      
      if (response.data.success) {
        toast.success('Proposal renamed successfully');
        setShowRenameModal(false);
        setSelectedProposal(null);
        setRenameValue('');
        onRefresh();
      } else {
        toast.error(response.data.error || 'Failed to rename proposal');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to rename proposal');
      console.error('Rename error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyClick = (proposal) => {
    if (!proposal.has_outline) {
      toast.error('Cannot copy proposal: outline has not been generated yet');
      return;
    }
    setSelectedProposal(proposal);
    setCopyValue(`${proposal.title}_copy`);
    setShowCopyModal(true);
  };

  const handleCopyConfirm = async () => {
    if (!selectedProposal || !copyValue.trim()) {
      toast.error('Please enter a valid name');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/proposal/plan/${encodeURIComponent(selectedProposal.title)}/copy`, {
        title: copyValue.trim()
      });
      
      if (response.data.success) {
        toast.success('Proposal copied successfully');
        setShowCopyModal(false);
        setSelectedProposal(null);
        setCopyValue('');
        onRefresh();
      } else {
        toast.error(response.data.error || 'Failed to copy proposal');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to copy proposal');
      console.error('Copy error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (proposal) => {
    setSelectedProposal(proposal);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProposal) return;

    setSubmitting(true);
    try {
      const response = await api.delete(`/proposal/plan/${encodeURIComponent(selectedProposal.title)}/delete`);
      
      if (response.data.success) {
        toast.success('Proposal deleted successfully');
        setShowDeleteModal(false);
        setSelectedProposal(null);
        onRefresh();
      } else {
        toast.error(response.data.error || 'Failed to delete proposal');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete proposal');
      console.error('Delete error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewProposal = () => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const newPlanId = `proposal_${timestamp}`;
    navigate(`/proposal-outline/${encodeURIComponent(newPlanId)}`);
  };

  if (loading) {
    return <div className="loading-state">Loading proposals...</div>;
  }

  return (
    <div className="card-content-area">
      <div className="card-header">
        <h2>My Proposals</h2>
        <div className="card-actions">
          <button 
            className="btn-primary"
            onClick={handleNewProposal}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={16} /> New Proposal
          </button>
          <button
            className="btn-secondary"
            onClick={onRefresh}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px' }}
            title="Refresh list"
          >
            <RefreshCw size={16} /> Refresh
          </button>
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search proposals..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="submitted">Submitted</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="status-summary">
        {Object.entries(statusCounts).map(([status, count]) => (
          <div key={status} className="status-badge">
            <span className="status-label">{status}</span>
            <span className="status-count">{count}</span>
          </div>
        ))}
      </div>

      <div className="proposals-list">
        {proposals.length === 0 ? (
          <div className="empty-state">
            <p>No proposals found.</p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Proposals you create will appear here. Make sure you're logged in as the proposal owner.
            </p>
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="empty-state">
            <p>No proposals match your search/filter criteria.</p>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Try adjusting your search term or status filter.
            </p>
          </div>
        ) : (
          filteredProposals.map((proposal) => (
            <div key={proposal.id} className="proposal-item">
              <div className="proposal-info">
                <h4>{proposal.display_name || proposal.title}</h4>
                <div className="proposal-meta">
                  {proposal.owner && proposal.owner.name && (
                    <span className="meta-item">Built by: {proposal.owner.name}</span>
                  )}
                  {proposal.client_name && (
                    <span className="meta-item">Client: {proposal.client_name}</span>
                  )}
                  {proposal.organization && (
                    <span className="meta-item">Org: {proposal.organization.name}</span>
                  )}
                  {proposal.team && (
                    <span className="meta-item">Team: {proposal.team.name}</span>
                  )}
                  <span className={`status-badge status-${proposal.status}`}>
                    {proposal.status}
                  </span>
                </div>
              </div>
              <div className="proposal-actions">
                <button 
                  className="btn-secondary"
                  onClick={() => navigate(`/proposal-outline/${encodeURIComponent(proposal.title)}`)}
                  title="Open proposal"
                >
                  Open
                </button>
                {proposal.can_edit && (
                  <>
                    <button
                      className="btn-icon"
                      onClick={() => handleRenameClick(proposal)}
                      title="Rename proposal"
                      style={{ padding: '8px', marginLeft: '4px' }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleCopyClick(proposal)}
                      title={proposal.has_outline ? "Copy proposal" : "Cannot copy: outline not generated"}
                      disabled={!proposal.has_outline}
                      style={{ 
                        padding: '8px', 
                        marginLeft: '4px',
                        opacity: proposal.has_outline ? 1 : 0.4,
                        cursor: proposal.has_outline ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDeleteClick(proposal)}
                      title="Delete proposal"
                      style={{ padding: '8px', marginLeft: '4px', color: '#ef4444' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Rename Modal */}
      {showRenameModal && selectedProposal && (
        <Modal onClose={() => !submitting && setShowRenameModal(false)}>
          <h2>Rename Proposal</h2>
          <div className="modal-form">
            <div className="form-group">
              <label>Proposal Name *</label>
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Enter new proposal name"
                disabled={submitting}
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowRenameModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleRenameConfirm}
                disabled={submitting || !renameValue.trim()}
              >
                {submitting ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Copy Modal */}
      {showCopyModal && selectedProposal && (
        <Modal onClose={() => !submitting && setShowCopyModal(false)}>
          <h2>Copy Proposal</h2>
          <div className="modal-form">
            <div className="form-group">
              <label>New Proposal Name *</label>
              <input
                type="text"
                value={copyValue}
                onChange={(e) => setCopyValue(e.target.value)}
                placeholder="Enter name for copied proposal"
                disabled={submitting}
                autoFocus
              />
              <small className="form-hint">
                This will create a copy of "{selectedProposal.title}" with all its outline and content.
              </small>
            </div>
            <div className="modal-actions">
              <button 
                className="btn-secondary" 
                onClick={() => setShowCopyModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleCopyConfirm}
                disabled={submitting || !copyValue.trim()}
              >
                {submitting ? 'Copying...' : 'Copy Proposal'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProposal && (
        <Modal onClose={() => !submitting && setShowDeleteModal(false)}>
          <h2>Delete Proposal</h2>
          <div className="modal-content">
            <p>
              Are you sure you want to delete <strong>"{selectedProposal.title}"</strong>?
            </p>
            <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px' }}>
              This action cannot be undone. This will delete the proposal, outline, and all content.
            </p>
          </div>
          <div className="modal-actions">
            <button 
              className="btn-secondary" 
              onClick={() => setShowDeleteModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleDeleteConfirm}
              disabled={submitting}
              style={{ background: '#ef4444', borderColor: '#ef4444' }}
            >
              {submitting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Organizations Management Card
const OrganizationsCard = ({ organizations, teams, loading, onRefresh }) => {
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showOrgDetailsModal, setShowOrgDetailsModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showTeamDetailsModal, setShowTeamDetailsModal] = useState(false);
  const [showTeamMembersModal, setShowTeamMembersModal] = useState(false);
  const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
  const [showAddOrgMemberModal, setShowAddOrgMemberModal] = useState(false);
  const [orgDetails, setOrgDetails] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  const [teamDetails, setTeamDetails] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [createOrgForm, setCreateOrgForm] = useState({ name: '', description: '' });
  const [createTeamForm, setCreateTeamForm] = useState({ name: '', description: '' });
  const [addTeamMemberForm, setAddTeamMemberForm] = useState({ email: '', role: 'member' });
  const [addOrgMemberForm, setAddOrgMemberForm] = useState({ email: '', role: 'member' });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [userOrgRole, setUserOrgRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleCreateOrg = async () => {
    if (!createOrgForm.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post('/orgs/', createOrgForm);
      toast.success('Organization created successfully!');
      setShowCreateOrgModal(false);
      setCreateOrgForm({ name: '', description: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create organization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewOrgDetails = async (orgId) => {
    try {
      const response = await api.get(`/orgs/${orgId}/`);
      setOrgDetails(response.data);
      setShowOrgDetailsModal(true);
    } catch (error) {
      toast.error('Failed to load organization details');
    }
  };

  const handleManageMembers = async (orgId) => {
    try {
      const response = await api.get(`/orgs/${orgId}/members/`);
      setOrgMembers(response.data.members || []);
      setSelectedOrg(orgId);
      
      // Get user's role in this org to determine if they can add members
      const orgResponse = await api.get(`/orgs/${orgId}/`);
      setUserOrgRole(orgResponse.data.role);
      
      setShowMembersModal(true);
    } catch (error) {
      toast.error('Failed to load members');
    }
  };

  const handleAddOrgMember = async () => {
    if (!selectedOrg) {
      toast.error('Please select an organization first');
      return;
    }
    if (!addOrgMemberForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/orgs/${selectedOrg}/members/add/`, {
        email: addOrgMemberForm.email,
        role: addOrgMemberForm.role
      });
      toast.success('Member added successfully!');
      setShowAddOrgMemberModal(false);
      setAddOrgMemberForm({ email: '', role: 'member' });
      // Refresh members list
      handleManageMembers(selectedOrg);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!selectedOrg) {
      toast.error('Please select an organization first');
      return;
    }
    if (!createTeamForm.name.trim()) {
      toast.error('Team name is required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/orgs/${selectedOrg}/teams/`, createTeamForm);
      toast.success('Team created successfully!');
      setShowCreateTeamModal(false);
      setCreateTeamForm({ name: '', description: '' });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create team');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTeamDetails = async (teamId) => {
    try {
      const response = await api.get(`/teams/${teamId}/`);
      setTeamDetails(response.data);
      setShowTeamDetailsModal(true);
    } catch (error) {
      toast.error('Failed to load team details');
    }
  };

  const handleManageTeamMembers = async (teamId) => {
    try {
      const response = await api.get(`/teams/${teamId}/members/`);
      setTeamMembers(response.data.members || []);
      setSelectedTeam(teamId);
      setShowTeamMembersModal(true);
    } catch (error) {
      toast.error('Failed to load team members');
    }
  };

  const handleAddTeamMember = async () => {
    if (!selectedTeam) {
      toast.error('Please select a team first');
      return;
    }
    if (!addTeamMemberForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/teams/${selectedTeam}/members/add/`, {
        email: addTeamMemberForm.email,
        role: addTeamMemberForm.role
      });
      
      // Verify response contains expected data
      if (response.data && response.data.email) {
        toast.success(`Member ${response.data.email} added successfully!`);
      setShowAddTeamMemberModal(false);
      setAddTeamMemberForm({ email: '', role: 'member' });
        // Refresh team members list to show the new member
      handleManageTeamMembers(selectedTeam);
      onRefresh();
      } else {
        console.error('Unexpected response format:', response.data);
        toast.error('Member added but response format unexpected. Please refresh to verify.');
        handleManageTeamMembers(selectedTeam);
      }
    } catch (error) {
      console.error('Error adding team member:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to add team member';
      toast.error(errorMessage);
      
      // If it's a validation error (400), don't refresh - let user fix the issue
      if (error.response?.status !== 400) {
        // For other errors, refresh to show current state
        handleManageTeamMembers(selectedTeam);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-state">Loading organizations...</div>;
  }

  return (
    <>
      <div className="card-content-area">
        <div className="card-header">
          <h2>Organizations & Teams</h2>
          <button className="btn-primary" onClick={() => setShowCreateOrgModal(true)}>
            <Plus size={16} /> Create Organization
          </button>
        </div>

        <div className="orgs-list">
          {organizations.length === 0 ? (
            <div className="empty-state">
              <p>You are not a member of any organizations yet.</p>
              <button className="btn-primary" onClick={() => setShowCreateOrgModal(true)}>
                Create Organization
              </button>
            </div>
          ) : (
            organizations.map((org) => (
              <div key={org.id} className="org-item">
                <div className="org-header">
                  <h3>{org.name}</h3>
                  <span className="role-badge">{org.role}</span>
                </div>
                <p className="org-description">{org.description || 'No description'}</p>
                <div className="org-actions">
                  <button className="btn-secondary" onClick={() => handleViewOrgDetails(org.id)}>
                    View Details
                  </button>
                  {org.role === 'admin' && (
                    <>
                      <button className="btn-secondary" onClick={() => handleManageMembers(org.id)}>
                        Manage Members
                      </button>
                      <button className="btn-secondary" onClick={() => {
                        setSelectedOrg(org.id);
                        setShowCreateTeamModal(true);
                      }}>
                        <Plus size={14} /> Create Team
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="teams-section">
          <h3>Teams</h3>
          {teams.length === 0 ? (
            <div className="empty-state">No teams found</div>
          ) : (
            <div className="teams-list">
              {teams.map((team) => (
                <div 
                  key={team.id} 
                  className="team-item clickable"
                  onClick={() => handleViewTeamDetails(team.id)}
                >
                  <div className="team-info">
                    <h4>{team.name}</h4>
                    <span className="org-name">{team.organization_name}</span>
                  </div>
                  <div className="team-actions-row">
                    <span className="role-badge">{team.role}</span>
                    <button 
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManageTeamMembers(team.id);
                      }}
                      title="Manage Members"
                    >
                      <Users size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <Modal onClose={() => setShowCreateOrgModal(false)}>
          <h2>Create Organization</h2>
          <div className="modal-form">
            <div className="form-group">
              <label>Organization Name *</label>
              <input
                type="text"
                value={createOrgForm.name}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, name: e.target.value })}
                placeholder="Enter organization name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={createOrgForm.description}
                onChange={(e) => setCreateOrgForm({ ...createOrgForm, description: e.target.value })}
                placeholder="Enter organization description"
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateOrgModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateOrg} disabled={submitting}>
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Organization Details Modal */}
      {showOrgDetailsModal && orgDetails && (
        <Modal onClose={() => setShowOrgDetailsModal(false)}>
          <h2>Organization Details</h2>
          <div className="modal-content">
            <div className="detail-item">
              <strong>Name:</strong> {orgDetails.name}
            </div>
            <div className="detail-item">
              <strong>Description:</strong> {orgDetails.description || 'N/A'}
            </div>
            <div className="detail-item">
              <strong>Teams:</strong> {orgDetails.teams_count || 0}
            </div>
            <div className="detail-item">
              <strong>Members:</strong> {orgDetails.members_count || 0}
            </div>
            <div className="detail-item">
              <strong>Created:</strong> {new Date(orgDetails.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-primary" onClick={() => setShowOrgDetailsModal(false)}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Manage Members Modal */}
      {showMembersModal && selectedOrg && (
        <Modal onClose={() => setShowMembersModal(false)}>
          <div className="modal-header-with-action">
            <h2>Manage Members</h2>
            {userOrgRole === 'admin' && (
              <button 
                className="btn-primary btn-small"
                onClick={() => {
                  setShowAddOrgMemberModal(true);
                }}
              >
                <Plus size={14} /> Add Member
              </button>
            )}
          </div>
          <div className="modal-content">
            {orgMembers.length === 0 ? (
              <div className="empty-state">No members found</div>
            ) : (
              <div className="members-list">
                {orgMembers.map((member) => (
                  <div key={member.user_id} className="member-item">
                    <div className="member-info">
                      <div>
                        <strong>{member.email}</strong>
                        {member.username && (
                          <div className="member-username">@{member.username}</div>
                        )}
                      </div>
                      <span className="role-badge">{member.role}</span>
                    </div>
                    <div className="member-meta">
                      Joined: {new Date(member.joined_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button className="btn-primary" onClick={() => setShowMembersModal(false)}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Add Organization Member Modal */}
      {showAddOrgMemberModal && selectedOrg && (
        <Modal onClose={() => setShowAddOrgMemberModal(false)}>
          <h2>Add Organization Member</h2>
          <div className="modal-form">
            <div className="form-group">
              <label>User Email *</label>
              <input
                type="email"
                value={addOrgMemberForm.email}
                onChange={(e) => setAddOrgMemberForm({ ...addOrgMemberForm, email: e.target.value })}
                placeholder="Enter user email"
              />
              <small className="form-hint">User must have an account in the system</small>
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={addOrgMemberForm.role}
                onChange={(e) => setAddOrgMemberForm({ ...addOrgMemberForm, role: e.target.value })}
              >
                <option value="member">Member</option>
                <option value="admin">Organization Admin</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAddOrgMemberModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAddOrgMember} disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Team Modal */}
      {showCreateTeamModal && selectedOrg && (
        <Modal onClose={() => setShowCreateTeamModal(false)}>
          <h2>Create Team</h2>
          <div className="modal-form">
            <div className="form-group">
              <label>Team Name *</label>
              <input
                type="text"
                value={createTeamForm.name}
                onChange={(e) => setCreateTeamForm({ ...createTeamForm, name: e.target.value })}
                placeholder="Enter team name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={createTeamForm.description}
                onChange={(e) => setCreateTeamForm({ ...createTeamForm, description: e.target.value })}
                placeholder="Enter team description"
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateTeamModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateTeam} disabled={submitting}>
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Team Details Modal */}
      {showTeamDetailsModal && teamDetails && (
        <Modal onClose={() => setShowTeamDetailsModal(false)}>
          <h2>Team Details</h2>
          <div className="modal-content">
            <div className="detail-item">
              <strong>Name:</strong> {teamDetails.name}
            </div>
            <div className="detail-item">
              <strong>Organization:</strong> {teamDetails.organization?.name || 'N/A'}
            </div>
            <div className="detail-item">
              <strong>Description:</strong> {teamDetails.description || 'N/A'}
            </div>
            <div className="detail-item">
              <strong>Members:</strong> {teamDetails.members_count || 0}
            </div>
            <div className="detail-item">
              <strong>Your Role:</strong> <span className="role-badge">{teamDetails.user_role || 'N/A'}</span>
            </div>
          </div>
          <div className="modal-actions">
            <button 
              className="btn-secondary" 
              onClick={() => {
                setShowTeamDetailsModal(false);
                handleManageTeamMembers(teamDetails.id);
              }}
            >
              Manage Members
            </button>
            <button className="btn-primary" onClick={() => setShowTeamDetailsModal(false)}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Team Members Modal */}
      {showTeamMembersModal && selectedTeam && (
        <Modal onClose={() => setShowTeamMembersModal(false)}>
          <div className="modal-header-with-action">
            <h2>Team Members</h2>
            <button 
              className="btn-primary btn-small"
              onClick={() => {
                setShowAddTeamMemberModal(true);
              }}
            >
              <Plus size={14} /> Add Member
            </button>
          </div>
          <div className="modal-content">
            {teamMembers.length === 0 ? (
              <div className="empty-state">No members found</div>
            ) : (
              <div className="members-list">
                {teamMembers.map((member) => (
                  <div key={member.user_id} className="member-item">
                    <div className="member-info">
                      <strong>{member.email}</strong>
                      <span className="role-badge">{member.role}</span>
                    </div>
                    <div className="member-meta">
                      Joined: {new Date(member.joined_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-actions">
            <button className="btn-primary" onClick={() => setShowTeamMembersModal(false)}>
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Add Team Member Modal */}
      {showAddTeamMemberModal && selectedTeam && (
        <Modal onClose={() => setShowAddTeamMemberModal(false)}>
          <h2>Add Team Member</h2>
          <div className="modal-form">
            <div className="form-group">
              <label>User Email *</label>
              <input
                type="email"
                value={addTeamMemberForm.email}
                onChange={(e) => setAddTeamMemberForm({ ...addTeamMemberForm, email: e.target.value })}
                placeholder="Enter user email"
              />
              <small className="form-hint">User must be a member of the organization first</small>
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={addTeamMemberForm.role}
                onChange={(e) => setAddTeamMemberForm({ ...addTeamMemberForm, role: e.target.value })}
              >
                <option value="member">Member</option>
                <option value="manager">Team Manager</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowAddTeamMemberModal(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleAddTeamMember} disabled={submitting}>
                {submitting ? 'Adding...' : 'Add Member'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

// Gamification Card
const GamificationCard = ({ userPoints, badges, loading }) => {
  if (loading) {
    return <div className="loading-state">Loading gamification data...</div>;
  }

  if (!userPoints) {
    return <div className="empty-state">No gamification data available</div>;
  }

  const progressPercentage = userPoints.points_to_next_level > 0
    ? ((userPoints.total_points % userPoints.points_to_next_level) / userPoints.points_to_next_level) * 100
    : 100;

  return (
    <div className="card-content-area">
      <div className="card-header">
        <h2>Your Progress</h2>
      </div>

      <div className="points-section">
        <div className="points-display">
          <div className="points-value">{userPoints.total_points}</div>
          <div className="points-label">Total Points</div>
        </div>
        <div className="level-display">
          <div className="level-value">Level {userPoints.current_level}</div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="progress-text">
            {userPoints.points_to_next_level} points to next level
          </div>
        </div>
        <div className="streak-display">
          <div className="streak-value">{userPoints.daily_usage_streak}</div>
          <div className="streak-label">Day Streak</div>
        </div>
      </div>

      <div className="statistics-section">
        <h3>Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-value">{userPoints.statistics.total_proposals}</div>
            <div className="stat-label">Proposals</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{userPoints.statistics.total_nodes_created}</div>
            <div className="stat-label">Nodes Created</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{userPoints.statistics.total_downloads}</div>
            <div className="stat-label">Downloads</div>
          </div>
        </div>
      </div>

      <div className="badges-section">
        <h3>Badges ({badges.length})</h3>
        {badges.length === 0 ? (
          <div className="empty-state">No badges earned yet</div>
        ) : (
          <div className="badges-grid">
            {badges.map((badge, idx) => (
              <div key={idx} className="badge-item">
                <div className="badge-icon">{badge.badge_icon || '🏆'}</div>
                <div className="badge-name">{badge.badge_name}</div>
                <div className="badge-description">{badge.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ children, onClose }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={20} />
        </button>
        {children}
      </div>
    </div>
  );
};

export default Management;

